/**
 * Core Chip Component
 *
 * Removable/clickable tag with variants, icons, and avatar support.
 * Used for filters, tag inputs, and selected items display.
 *
 * @example
 *   <Chip label="React" onDelete={() => remove('react')} />
 *   <Chip label="John" avatar={<Avatar name="John" size="xs" />} variant="outlined" />
 */
import React from 'react';
import { Chip as MuiChip } from '@mui/material';

export interface ChipProps {
  label: string;
  /** Visual variant */
  variant?: 'filled' | 'outlined';
  /** Color */
  color?: 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';
  /** Size */
  size?: 'small' | 'medium';
  /** Show delete button */
  onDelete?: () => void;
  /** Click handler — makes chip clickable */
  onClick?: () => void;
  /** Icon before label */
  icon?: React.ReactElement;
  /** Avatar element before label */
  avatar?: React.ReactElement;
  /** Disabled */
  disabled?: boolean;
  className?: string;
}

export const Chip: React.FC<ChipProps> = ({
  label,
  variant = 'filled',
  color = 'default',
  size = 'small',
  onDelete,
  onClick,
  icon,
  avatar,
  disabled = false,
  className = '',
}) => (
  <MuiChip
    label={label}
    variant={variant}
    color={color}
    size={size}
    onDelete={onDelete}
    onClick={onClick}
    icon={icon}
    avatar={avatar}
    disabled={disabled}
    className={className}
    sx={{ borderRadius: '8px', fontWeight: 500 }}
  />
);
export default Chip;
